/**
 * Unit 3.IMPL — DragPreview rebuild (insurescope-zkolv.1.3.3).
 *
 * Pure-Tailwind preview card rendered into a detached root by the
 * `attachDraggable` recipe in `DraggableRow.tsx`. Snapshot-only — the browser
 * captures this DOM tree synchronously at gesture start, so any state changes
 * after mount are invisible. Keep it minimal.
 *
 * Visual contract — see docs/design-briefs/2026-05-19-drag-preview.md
 *   - container: `bg-card ring-1 ring-foreground/10 shadow-md rounded-xl`
 *   - count badge (only when count > 1): `bg-brand-copper ring-2 ring-popover`
 *   - name truncates at 200px with the full name in the `title` attribute
 *   - count overflow: literal numeric (no `+` prefix) — see RED test [3]
 *
 * @see DraggableRow.tsx — owns the createRoot/flushSync/setTimeout-unmount
 *   recipe that mounts THIS component into a detached snapshot container.
 */
import { cn } from "@/lib/utils";

import {
    getLibraryItemIcon,
    type LibraryItemTypeKey,
} from "../utils/libraryItemTypes";

interface DragPreviewProps {
    name: string;
    type: LibraryItemTypeKey;
    count: number;
    isKnowledgeBase?: boolean;
}

export const DragPreview = ({
    name,
    type,
    count,
    isKnowledgeBase,
}: DragPreviewProps) => {
    const typeKey: LibraryItemTypeKey =
        type === "folder" && isKnowledgeBase ? "knowledge_base" : type;
    const icon = getLibraryItemIcon(typeKey);

    return (
        // Padding extends the drag-image snapshot box so it doesn't clip the
        // badge's outward ring (-top-2 -right-2 + ring-2 = 10px overhang) or
        // the card's ring-1. See setCustomNativeDragPreview in dnd hook.
        <div className="inline-block p-3">
            <div
                data-slot="drag-preview"
                className={cn(
                    "relative inline-flex items-center gap-2 px-3 py-2",
                    "max-w-[280px] rounded-xl",
                    "bg-card ring-foreground/10 shadow-md ring-1"
                )}
            >
                {icon}
                <span
                    data-slot="drag-preview-name"
                    className="text-foreground max-w-[200px] truncate text-sm font-medium"
                    title={name}
                >
                    {name}
                </span>
                {count > 1 && (
                    <span
                        data-slot="drag-preview-badge"
                        className={cn(
                            "absolute -top-2 -right-2",
                            "inline-flex items-center justify-center",
                            "h-5 min-w-5 px-1.5",
                            "bg-brand-copper text-brand-copper-foreground rounded-full",
                            "ring-popover ring-2",
                            "text-[0.7rem] leading-none font-bold"
                        )}
                    >
                        {count}
                    </span>
                )}
            </div>
        </div>
    );
};
